import { computed, getCurrentInstance, Ref } from 'vue';
import { EventRefined } from '@fullcalendar/core/internal';
import { eventResponseStatus } from '../../../shared/enums/eventResponseStatus';
import { calendarUtil } from '../../../shared/utils/calendarUtil';
import { types } from '../../store/types/types.js';
import { notificationTypes } from '../../../shared/enums/notificationTypes';
import { iconClassEnum } from '../../../shared/enums/iconClassEnum';
import { eventTypeEnum } from '../../../shared/enums/eventTypeEnum';
import { DateTimeUtil } from '../../../shared/utils/dateTimeUtil';
import { lessonStatus } from '../../../shared/enums/lessonStatus';
import { teacherTypes } from '../../../shared/enums/teacherTypes';

export function useCalendarViewTimelineViewEvent(
  eventModel: Ref<EventRefined>,
  isStart: Ref<boolean>,
  isEnd: Ref<boolean>
) {
  const instance = getCurrentInstance();
  if (!instance?.proxy) {
    throw new Error('useCalendarViewTimelineViewEvent must be called within setup()');
  }
  const { proxy } = instance;

  const notifications = computed(() => proxy.$store.getters[types.GET_NOTIFICATIONS] ?? []);

  const status = computed((): eventResponseStatus => eventModel.value.extendedProps.responseStatus);

  const isPrivate = computed(() => eventModel.value.extendedProps.private);

  const isRecurring = computed(() => eventModel.value.extendedProps.repeating != null);

  const isRecurringException = computed(() => {
    if (eventModel.value.extendedProps.repeating == null) {
      return false;
    }
    const { startDateTime, endDateTime } = eventModel.value.extendedProps;
    const { originalStartDateTime, originalEndDateTime } = eventModel.value.extendedProps.repeating;
    return (
      !DateTimeUtil.isSameTime(startDateTime, originalStartDateTime) ||
      !DateTimeUtil.isSameTime(endDateTime, originalEndDateTime)
    );
  });

  const hasNote = computed(() => Boolean(eventModel.value.extendedProps.lesson?.hasRelevantNote));

  const lesson = computed(() => eventModel.value.extendedProps?.lesson);

  const invitedGroups = computed(() => eventModel.value.extendedProps?.invitedGroups);

  const isLesson = computed(
    (): boolean =>
      eventModel.value.extendedProps?.type === eventTypeEnum.LESSON && Boolean(eventModel.value.extendedProps?.lesson)
  );

  const mainGroup = computed(() => invitedGroups.value?.map(group => group.name).join(', ') ?? '');

  const teacher = computed(() => {
    const lessonVal = lesson.value;
    if (!lessonVal?.participants || lessonVal.participants.length === 0) {
      return '';
    }

    const substituteTeachers = lessonVal.participants
      .filter(participant => participant.participantRole === teacherTypes.SUBSTITUTE)
      .map(participant => participant.teacherInitials);

    if (substituteTeachers.length > 0) {
      return substituteTeachers.join(', ');
    }

    const otherTeachers = lessonVal.participants
      .filter(
        participant =>
          participant.participantRole === teacherTypes.PRIMARY ||
          participant.participantRole === teacherTypes.HELP ||
          participant.participantRole === teacherTypes.PEDAGOGUE
      )
      .map(participant => participant.teacherInitials);

    return otherTeachers.join(', ');
  });

  const extendedInfo = computed(() => {
    const location = calendarUtil.getPrimaryLocation(eventModel.value.extendedProps);
    const parts = [location];
    if (isLesson.value) {
      parts.push(mainGroup.value, teacher.value);
    }
    return parts.join(', ');
  });

  const tooltip = computed(() => [eventModel.value.title, extendedInfo.value].join('\n'));

  const layout = computed(() => {
    if (eventModel.value.allDay) {
      return 'compact';
    }
    const { start, end } = eventModel.value;
    const startDate = start as string | Date;
    const endDate = end as string | Date;
    const isEventStart = isStart.value;
    const isEventEnd = isEnd.value;
    // referenceDay selects which day's midnight boundaries clip the duration:
    // the start day for start segments, the end day for end-only segments.
    const referenceDay = isEventStart ? startDate : endDate;
    const durationInMinutes =
      !isEventStart && !isEventEnd
        ? 24 * 60
        : DateTimeUtil.getDateDifferenceOnDay(startDate, endDate, referenceDay);
    if (durationInMinutes < 45) {
      return 'compact';
    }
    if (durationInMinutes < 75) {
      return 'small';
    }
    if (durationInMinutes < 90) {
      return 'medium';
    }
    return 'large';
  });

  const indicator = computed(() => {
    switch (status.value) {
      case eventResponseStatus.ACCEPTED:
        return 'accepted';
      case eventResponseStatus.TENTATIVE:
        return 'tentative';
      case eventResponseStatus.WAITING:
        return 'waiting';
      default:
        return 'none';
    }
  });

  const hasNotification = computed(() =>
    notifications.value.some(
      notification =>
        eventModel.value.extendedProps.eventId &&
        notification.eventId == eventModel.value.extendedProps.eventId &&
        notification.notificationType === notificationTypes.BADGE
    )
  );

  const title = computed(() => eventModel.value.title);

  const metadata = computed(() => {
    if (!eventModel.value.extendedProps.metadata) {
      return '';
    }
    return `(${eventModel.value.extendedProps.metadata})`;
  });

  const tag = computed(() => {
    if (lesson.value?.lessonStatus == lessonStatus.SUBSTITUTE) {
      return proxy.$t('calendar.substitute_teacher') as string;
    }
    return '';
  });

  return {
    iconClassEnum,
    tooltip,
    layout,
    isPrivate,
    isRecurring,
    isRecurringException,
    hasNote,
    indicator,
    extendedInfo,
    hasNotification,
    title,
    metadata,
    tag,
  };
}
